import type {
  BlockChypClient,
  BlockChypCredentials,
} from 'src/utils/Services/blockchyp.service';

import type { RuntimeKind } from '../types';
import { RUNTIME_CAPABILITIES } from '../capabilities';
import { createElectronBlockChypClient } from '../electron/payments.electron';
import { electronDispatchReconciler } from '../electron/dispatch-reconcile.electron';
import { createElectronBlockChypLocalTransport } from '../electron/payments-lan.electron';
import type {
  PlatformPayments,
  BlockChypLocalTransport,
  BlockChypClientExecution,
  DesktopDispatchReconciler,
} from './types';

/**
 * PlatformPayments (PLAN §5.5 + AMENDMENT A1/A2).
 *
 * Phase 1's payment surface here is: the capability answers the checkout reads,
 * plus the *client factory* seam A1.2 identifies. The shared V4/V5 checkout
 * orchestration is untouched on every runtime — the only platform difference is
 * where the provider call executes.
 *
 * On Electron `createBlockChypClient` returns an **IPC-backed client
 * implementing the same interface** (T3.B). The signing key lives in the
 * Electron main process and never reaches the renderer (A1.3/A1.4); the
 * credentials pass through `pc:payments:provision-credentials` on their way into
 * main's `safeStorage` custody and are never returned.
 *
 * `blockchypLocalTransport()` is `null` on iOS, Android and web: the iOS LAN
 * path keeps using the native plugin directly through its existing,
 * test-locked guards (PLAN §5.5), and Android/web have no LAN capability. On
 * **Electron** (Phase W, Workstream A) it returns the main-process LAN
 * transport — the same plugin-shaped factory the shared LAN flows already
 * resolve through this seam, executing in main over allow-listed IPC.
 */
let sharedElectronLocalTransport: BlockChypLocalTransport | null = null;

/** Stateless, so one instance serves every caller. */
function electronLocalTransport(): BlockChypLocalTransport {
  sharedElectronLocalTransport ??= createElectronBlockChypLocalTransport();
  return sharedElectronLocalTransport;
}

export function createPlatformPayments(kind: RuntimeKind): PlatformPayments {
  const capabilities = RUNTIME_CAPABILITIES[kind];
  const blockchypClientExecution: BlockChypClientExecution =
    kind === 'electron' ? 'main-process-ipc' : 'renderer-official-sdk';

  return {
    manualCardEntry: capabilities.manualCardEntry,
    completedReportPlatform: capabilities.completedReportPlatform,
    localTerminalPing: capabilities.localTerminalPing,
    blockchypCloudProbe: capabilities.blockchypCloudProbe,
    blockchypLocalNetwork: capabilities.blockchypLocalNetwork,
    stripeM2: capabilities.stripeM2,
    blockchypClientExecution,

    createBlockChypClient: async (credentials: BlockChypCredentials): Promise<BlockChypClient> => {
      if (blockchypClientExecution === 'main-process-ipc') {
        // Desktop BlockChyp executes in the Electron main process (T3.B); the
        // renderer never constructs a signing client. Provisions the credentials
        // into main and returns the IPC-backed client over the allow-listed
        // `pc:payments:*` channels.
        return createElectronBlockChypClient(credentials);
      }
      // Mobile and web keep today's renderer-side official SDK, byte for byte.
      // Imported lazily so the payments SDK never enters the boot module graph.
      const { createClient } = await import('src/utils/Services/blockchyp.service');
      return createClient(credentials);
    },

    blockchypLocalTransport: (): BlockChypLocalTransport | null =>
      kind === 'electron' && capabilities.blockchypLocalNetwork ? electronLocalTransport() : null,

    // T4-07: only a runtime with a main-process dispatch journal can reconcile one.
    desktopDispatchReconciler: (): DesktopDispatchReconciler | null =>
      capabilities.desktopDispatchReconcile ? electronDispatchReconciler() : null,
  };
}
